import React, { useState, useContext } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import { CUSTOM_ATTRIBUTE_CHECKBOX_LABEL, CUSTOM_ATTRIBUTE_TOOLTIP } from 'Common/CustomAttribute';
import ModalWrapper from './ModalWrapper';
import { AppContext } from 'Context/AppContext';
import { createAttribute } from 'api/DataReview';
import Utils from 'Common/Utils';

/**
 * #6300 attribute-name rules, shared with the Self-Service Taxonomy create form
 * (Components/GMS/Attribute/AddEditViewAttr.jsx). 2–60 chars and the allowed
 * special-character set & / () [] % - _ . — validated via Utils.validate so every
 * attribute-creation surface (Catalog Management Review + Data Mapping) stays in
 * lockstep. WI-12684.
 */
const NAME_VALIDATION_OPTIONS = {
  name: { required: true, minLength: 2, maxLength: 60, type: 'alnum_with_spaces_dots' },
};
const NAME_HELPER_TEXT =
  'Name must be 2-60 characters long. Allowed characters: letters, numbers, spaces, and these symbols: & / () [] % - _ .';

interface CreateAttributeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, description: string) => void;
  /** Product name for the info bar. Only required when showInfoBar is true. */
  productName?: string;
  /** Category display name for the info bar. Only required when showInfoBar is true. */
  category?: string;
  /**
   * Category UID is sent to the backend as `category_id`. When empty/undefined
   * the backend creates the Attribute node without a Category→Attribute edge —
   * the Mapping tabs use this because they have no single category context.
   */
  categoryUid?: string;
  csKey: string;
  /**
   * Show the "Product Name / Category" info bar at the top of the modal.
   * Defaults to true for the Create Product flow; callers on the Mapping tabs
   * pass false so the modal reads as a stand-alone "Create Attribute" form.
   */
  showInfoBar?: boolean;
  /**
   * Attribute names already picked in other rows of the Create-Product modal.
   * The Add button is blocked if the typed name matches one of these (case-
   * insensitive) so we don't end up with two rows pointing at the same
   * attribute. The backend's create_attribute_for_category is idempotent, so
   * without this guard a duplicate submission would succeed and silently
   * produce two rows with the same name.
   */
  existingAttributeNames?: string[];
}

const CreateAttributeModal = ({
  isOpen,
  onClose,
  onCreate,
  productName = '',
  category = '',
  categoryUid = '',
  csKey,
  showInfoBar = true,
  existingAttributeNames = [],
}: CreateAttributeModalProps) => {
  const appData = useContext(AppContext);
  const [attributeName, setAttributeName] = useState('');
  const [description, setDescription] = useState('');
  // AB#14524 "Mark as custom attribute" -- default unchecked (AC 4). Sent as the
  // backend's `is_custom` boolean; this one modal serves Data Review and Data Mapping.
  const [isCustom, setIsCustom] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreate = async () => {
    if (!isValid) return;

    const industryId = appData?.selectedIndustry?.id;
    if (!industryId) {
      toast.error('Industry not selected');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createAttribute(csKey, {
        industry_id: industryId,
        attribute: attributeName.trim(),
        // Empty categoryUid signals the backend to skip the Category→Attribute
        // edge (used from the Mapping tabs which have no single category context).
        category_id: categoryUid || '',
        description: description.trim() || undefined,
        category_name: category || undefined,
        is_custom: isCustom,
      });

      if (response.done) {
        if (response.created) {
          toast.success(response.message || `Attribute '${attributeName}' created successfully`);
        } else {
          toast.info(response.message || `Attribute '${attributeName}' already exists`);
        }
        onCreate(attributeName.trim(), description.trim());
        handleClose();
      } else {
        toast.error(response.message || 'Failed to create attribute');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create attribute');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setAttributeName('');
    setDescription('');
    setIsCustom(false);
    onClose();
  };

  const trimmedName = attributeName.trim();
  const isDuplicate =
    trimmedName.length > 0 &&
    existingAttributeNames.some((n) => n.toLowerCase() === trimmedName.toLowerCase());
  // Utils.validate returns a [valid, message] tuple, but its JSDoc declares a
  // `boolean` return — cast through `unknown` to reach the real tuple shape.
  const [isNameValid, nameValidationMessage] = Utils.validate(
    { name: trimmedName },
    NAME_VALIDATION_OPTIONS
  ) as unknown as [boolean, string];
  // Only surface the inline error once the user has typed something — an empty
  // field shows the muted helper text, not a red error (no page clear, no data loss).
  const nameError = trimmedName.length > 0 && !isNameValid ? nameValidationMessage : '';
  const showNameError = nameError !== '' || isDuplicate;
  const isValid = isNameValid && !isDuplicate;

  const renderInfoBar = () => (
    <div className="tw-rounded tw-mb-4 tw-flex tw-gap-4">
      <div className="tw-flex-1 tw-flex tw-items-center tw-gap-1">
        <Typography variant="caption-metadata" className="tw-text-ink-secondary tw-shrink-0">
          Product Name:
        </Typography>
        <Typography variant="body-primary" className="tw-text-ink tw-truncate">
          {productName}
        </Typography>
      </div>
      <div className="tw-flex-1 tw-flex tw-items-center tw-gap-1">
        <Typography variant="caption-metadata" className="tw-text-ink-secondary tw-shrink-0">
          Category:
        </Typography>
        <Typography variant="body-primary" className="tw-text-ink tw-truncate">
          {category}
        </Typography>
      </div>
    </div>
  );

  const renderAttributeNameInput = () => (
    <div className="tw-mb-6">
      <Typography variant="sub-title-label" className="tw-text-ink tw-mb-2">
        Enter Name <span className="tw-text-tone-danger-ink">*</span>
      </Typography>
      <input data-testid="modals-createattributemodal-input-1"
        type="text"
        value={attributeName}
        onChange={(e) => setAttributeName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
        placeholder="Enter Attribute Name"
        className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded tw-text-body-primary focus:tw-outline-none focus:tw-ring-1 ${
          showNameError
            ? 'tw-border-tone-danger-glyph focus:tw-border-tone-danger-glyph focus:tw-ring-tone-danger-glyph'
            : 'tw-border-edge focus:tw-border-accent-hover focus:tw-ring-accent-hover'
        }`}
      />
      <Typography
        variant="caption-metadata"
        className={`tw-mt-1 ${nameError ? 'tw-text-tone-danger-ink' : 'tw-text-ink-secondary'}`}
      >
        {nameError || NAME_HELPER_TEXT}
      </Typography>
      {isDuplicate && (
        <Typography variant="caption-metadata" className="tw-text-tone-danger-ink tw-mt-1">
          This attribute is already in your product. Pick it from the dropdown instead of creating a duplicate.
        </Typography>
      )}
    </div>
  );

  const renderDescriptionInput = () => (
    <div className="tw-mb-6">
      <Typography variant="sub-title-label" className="tw-text-ink tw-mb-2">
        Description
      </Typography>
      <input data-testid="modals-createattributemodal-input-2"
        type="text"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Enter Description"
        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-edge tw-rounded tw-text-body-primary focus:tw-outline-none focus:tw-border-accent-hover focus:tw-ring-1 focus:tw-ring-accent-hover"
      />
    </div>
  );

  // Directly below Description, identical to the Attributes-module form (AC 1-3 of AB#14524).
  const renderCustomCheckbox = () => (
    <div className="tw-mb-6 tw-flex tw-items-center tw-gap-2">
      <Checkbox
        label={CUSTOM_ATTRIBUTE_CHECKBOX_LABEL}
        size="sm"
        checked={isCustom}
        onChange={(checked) => setIsCustom(checked)}
        data-testid="modals-createattributemodal-custom-checkbox"
      />
      <InfoTooltip
        placement="top"
        message={CUSTOM_ATTRIBUTE_TOOLTIP}
        data-testid="modals-createattributemodal-custom-info"
      />
    </div>
  );

  const renderFooter = () => (
    <div className="tw-flex tw-justify-end">
      <Button variant="primary" onClick={handleCreate} disabled={!isValid || isSubmitting} data-testid="catalog-create-attribute-submit-btn">
        {isSubmitting ? 'Creating...' : 'Add Attribute'}
      </Button>
    </div>
  );

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Attribute"
      footer={renderFooter()}
      width="normal"
      showCloseButton={false}
      backButton
    >
      <div>
        {showInfoBar && renderInfoBar()}
        {renderAttributeNameInput()}
        {renderDescriptionInput()}
        {renderCustomCheckbox()}
      </div>
    </ModalWrapper>
  );
};

export default CreateAttributeModal;
